/**
 * Used to load custom webfonts onto a page in a manner that's
 * most beneficial to user experience (including performance)
 * @module loadFonts
 */

/**
 * Imports Promises polyfill, so the API can always return a Promise
 */
import FontFaceObserver from 'fontfaceobserver/fontfaceobserver';

export { FontFaceObserver };

/**
 * Feature detection for the new CSS property `font-display`,
 * which can provide the ideal experience.
 * So if it's supported, return and let it work.
 * http://caniuse.com/#feat=css-font-rendering-controls
 * @type {boolean}
 */
const supportsFontDisplay = () => document.body.style.fontDisplay !== undefined;

/**
 * Feature detection for javascript `Promise`, used by FontFaceObserver.
 * The only browser we support that doesn't support Promises is IE
 * http://caniuse.com/#search=Promise
 * However IE's default behavior is already what we want:
 * it initially shows the fallback font & shows the webfont once it loads
 * @type {boolean}
 */
const doesNotSupportPromises = typeof Promise === 'undefined' ||
  Promise.toString().indexOf('[native code]') === -1;

/**
 * Default Options for custom fonts
 * @namespace
 * @property {string} language - user's language preference
 * @property {string} storageName - name used with sessionStorage
 * @property {string} notLoadedClass - className applied to html when fonts are not loaded
 * @property {string} mainLoadedClass - className applied to html when main fonts are loaded
 * @property {boolean} hasNativeSupport - whether we should allow the browser to load naturally
 * @property {HTMLElement} wrap - wrapper element where fonts are being used (likely html)
 * @property {Class} Observer - class used to observer font loading
 */
export const DEFAULT_OPTIONS = {
  language: 'en',
  storageName: 'fontsLoaded',
  notLoadedClass: 'fonts-not-loaded',
  mainLoadedClass: 'main-fonts-loaded',
  hasNativeSupport: () => supportsFontDisplay() || doesNotSupportPromises,
  wrap: document.documentElement,
  Observer: FontFaceObserver,
};

/**
 * Helper function for when we want to show the fonts
 * @param {HTMLElement} el - wrapper element from which to remove the className
 * @param {string} className - class to remove from html to show fonts
 * @param {string} key - sessionStorage key to set
 * @param {string} val - sessionStorage value to set
 */
const showFonts = (el, className, key, val) => {
  el.classList.remove(className);
  sessionStorage.setItem(key, val);
};

/**
 * Groups fonts into "main" and "deferred"
 * @param {array} fonts - list of all fonts for this language
 * @return {object} "main" and "deferred" groups
 */
const groupFonts = (fonts) => {
  const main = fonts.filter(font => !font.defer);
  const deferred = fonts.filter(font => font.defer);

  return { main, deferred };
};

/**
 * Applies the load method to a group of fonts
 * @param {array} fonts - group of fonts to load
 * @param {Class} Observer - class used to observer font loading
 * @return {array} array of promises
 */
const loadFontGroup = (fonts, Observer) =>
  fonts.map((font) => {
    const { family } = font;
    const newObserver = new Observer(family, font);

    return newObserver.load();
  });

/**
 * Observes fonts loading, using FontFaceObserver
 * @param {object} fontsByLanguage - group of fonts to use, from font groups
 * @param {object} [options] - additional options on how to handle font loading
 * @see DEFAULT_OPTIONS
 */
export default function loadFonts(fontsByLanguage, options = {}) {
  const {
    language,
    storageName,
    notLoadedClass,
    mainLoadedClass,
    hasNativeSupport,
    wrap,
    Observer,
  } = $.extend({}, DEFAULT_OPTIONS, options);

  // helper function to ensure consistency whenever we find success
  const success = () => showFonts(wrap, notLoadedClass, storageName, language);

  // if the browser can load natively, we're done
  if (hasNativeSupport()) {
    success();
    return Promise.resolve();
  }

  // choose fonts by language
  const fonts = fontsByLanguage[language] || [];

  // when no custom fonts are found for the language, we're done
  if (!fonts.length) {
    success();
    return Promise.resolve();
  }

  // group fonts for loading
  const { main, deferred } = groupFonts(fonts);

  // observer fonts loading, and show by group
  return Promise.all(loadFontGroup(main, Observer))
    .then(() => {
      // show main fonts
      wrap.classList.add(mainLoadedClass);
      // load deferred fonts
      return Promise.all(loadFontGroup(deferred, Observer));
    })
    .then(() => {
      // show all fonts
      wrap.classList.remove(mainLoadedClass);
      success();
    });
}


// WEBPACK FOOTER //
// ./styleguide/src/assets/base/load-fonts.js